import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../context/API';

export interface OverviewMetric {
  value: number;
  changePercentage: number;
  progress: number;
}

export interface OverviewData {
  totalSales: OverviewMetric;
  totalOrders: OverviewMetric;
  averageOrderValue: OverviewMetric;
  totalCustomers: OverviewMetric;
  salesAnalytics: OverviewMetric;
  categorySales: OverviewMetric;
  itemSales: OverviewMetric;
  trendingItems: OverviewMetric;
  disputes: OverviewMetric;
}

export interface OverviewApiResponse {
  message: string;
  status: number;
  data: OverviewData;
  success: boolean;
}

// Types for Sales Trends API Response
export interface SalesTrendDataPoint {
  date: string;
  sales: number;
  orders: number;
}

export interface SalesTrendsPeriod {
  startDate: string;
  endDate: string;
}

export interface SalesTrendsData {
  sales: SalesTrendDataPoint[];
  period: SalesTrendsPeriod;
}

export interface SalesTrendsApiResponse {
  message: string;
  status: number;
  data: SalesTrendsData;
  success: boolean;
}

// Types for Categories API Response
export interface CategorySalesData {
  name: string | null;
  sales: number;
}

export interface CategoriesData {
  categories: CategorySalesData[];
  totalSales: number;
}

export interface CategoriesApiResponse {
  message: string;
  status: number;
  data: CategoriesData;
  success: boolean;
}

// Types for Top Items API Response
export interface TopItemData {
  name: string;
  revenue: number;
  categoryName: string;
}

export interface TopItemsData {
  items: TopItemData[];
  totalRevenue: number;
}

export interface TopItemsApiResponse {
  message: string;
  status: number;
  data: TopItemsData;
  success: boolean;
}

interface MerchantAnalyticsOverviewState {
  overviewData: OverviewData | null;
  salesTrendsData: SalesTrendsData | null;
  categoriesData: CategoriesData | null;
  topItemsData: TopItemsData | null;
  loading: boolean;
  salesTrendsLoading: boolean;
  categoriesLoading: boolean;
  topItemsLoading: boolean;
  error: string | null;
}

const initialState: MerchantAnalyticsOverviewState = {
  overviewData: null,
  salesTrendsData: null,
  categoriesData: null,
  topItemsData: null,
  loading: false,
  salesTrendsLoading: false,
  categoriesLoading: false,
  topItemsLoading: false,
  error: null,
};

export const getDefaultDateRange = () => {
  const now = new Date();
  const startDate = new Date();
  startDate.setDate(now.getDate() - 7);
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0],
  };
};

export const fetchOverviewData = createAsyncThunk<
  OverviewData,
  { startDate: string; endDate: string } | void,
  { rejectValue: string }
>('merchantAnalyticsOverview/fetchOverviewData', async (dateRange, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const { startDate, endDate } = dateRange || getDefaultDateRange();    
    const response = await API.get<OverviewApiResponse>('/analytics/overview', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate,
        endDate,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('Failed to fetch overview data');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch overview data'
    );
  }
});

export const fetchSalesTrendsData = createAsyncThunk<
  SalesTrendsData,
  { startDate: string; endDate: string } | void,
  { rejectValue: string }
>('merchantAnalyticsOverview/fetchSalesTrendsData', async (dateRange, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');    
    const { startDate, endDate } = dateRange || getDefaultDateRange();
    const response = await API.get<SalesTrendsApiResponse>('/analytics/sales-trends', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate,
        endDate,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('Failed to fetch sales trends data');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch sales trends data'
    );
  }
});

export const fetchCategoriesData = createAsyncThunk<
  CategoriesData,
  { startDate: string; endDate: string } | void,
  { rejectValue: string }
>('merchantAnalyticsOverview/fetchCategoriesData', async (dateRange, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');    
    const { startDate, endDate } = dateRange || getDefaultDateRange();
    const response = await API.get<CategoriesApiResponse>('/analytics/categories', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate,
        endDate,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('Failed to fetch categories data');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch categories data'
    );
  }
});

export const fetchTopItemsData = createAsyncThunk<
  TopItemsData,
  { startDate: string; endDate: string } | void,
  { rejectValue: string }
>('merchantAnalyticsOverview/fetchTopItemsData', async (dateRange, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');    
    const { startDate, endDate } = dateRange || getDefaultDateRange();
    
    const response = await API.get<TopItemsApiResponse>('/analytics/top-items', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate,
        endDate,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('Failed to fetch top items data');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch top items data'
    );
  }
});

const merchantAnalyticsOverviewSlice = createSlice({
  name: 'merchantAnalyticsOverview',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearOverviewData: (state) => {
      state.overviewData = null;
    },
    clearSalesTrendsData: (state) => {
      state.salesTrendsData = null;
    },
    clearCategoriesData: (state) => {
      state.categoriesData = null;
    },
    clearTopItemsData: (state) => {
      state.topItemsData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverviewData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOverviewData.fulfilled, (state, action) => {
        state.loading = false;
        state.overviewData = action.payload;
      })
      .addCase(fetchOverviewData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch sales trends data
      .addCase(fetchSalesTrendsData.pending, (state) => {
        state.salesTrendsLoading = true;
        state.error = null;
      })
      .addCase(fetchSalesTrendsData.fulfilled, (state, action) => {
        state.salesTrendsLoading = false;
        state.salesTrendsData = action.payload;
      })
      .addCase(fetchSalesTrendsData.rejected, (state, action) => {
        state.salesTrendsLoading = false;
        state.error = action.payload as string;
      })
      // Fetch categories data
      .addCase(fetchCategoriesData.pending, (state) => {
        state.categoriesLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoriesData.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesData = action.payload;
      })
      .addCase(fetchCategoriesData.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.error = action.payload as string;
      })
      // Fetch top items data
      .addCase(fetchTopItemsData.pending, (state) => {
        state.topItemsLoading = true;
        state.error = null;
      })
      .addCase(fetchTopItemsData.fulfilled, (state, action) => {
        state.topItemsLoading = false;
        state.topItemsData = action.payload;
      })
      .addCase(fetchTopItemsData.rejected, (state, action) => {
        state.topItemsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearOverviewData, clearSalesTrendsData, clearCategoriesData, clearTopItemsData } = merchantAnalyticsOverviewSlice.actions;
export default merchantAnalyticsOverviewSlice.reducer;
